import { useState, useCallback } from 'react';

interface UndoRedoState<T> {
  state: T;
  setState: (updater: (prev: T) => T | T) => void;
  // Aktualizacja bez zapisywania do historii (np. ciągły drag/pan)
  setStateSilent: (updater: (prev: T) => T | T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (newInitial: T) => void;
}

const MAX_HISTORY = 50;

export function useUndoRedo<T>(initial: T): UndoRedoState<T> {
  const [past, setPast] = useState<T[]>([]);
  const [present, setPresent] = useState<T>(initial);
  const [future, setFuture] = useState<T[]>([]);

  const setState: UndoRedoState<T>["setState"] = useCallback(
    (value) => {
      setPast(prev => {
        const newPast = [...prev, present];
        // limit length
        if (newPast.length > MAX_HISTORY) newPast.shift();
        return newPast;
      });
      setFuture([]);
      setPresent(prev => (typeof value === 'function' ? (value as (p: T) => T)(prev) : value));
    },
    [present]
  );

  const setStateSilent: UndoRedoState<T>["setStateSilent"] = useCallback(
    (value) => {
      setPresent(prev => (typeof value === 'function' ? (value as (p: T) => T)(prev) : value));
    },
    []
  );

  const undo = useCallback(() => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    setPast(prev => prev.slice(0, prev.length - 1));
    setFuture(f => [present, ...f]);
    setPresent(previous);
  }, [past, present]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture(f => f.slice(1));
    setPast(prev => [...prev, present]);
    setPresent(next);
  }, [future, present]);

  const reset = useCallback((newInitial: T) => {
    setPast([]);
    setFuture([]);
    setPresent(newInitial);
  }, []);

  return {
    state: present,
    setState,
    setStateSilent,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    reset,
  };
} 